/* PDP Community Recommends
   Scoped web component. Initializes the carousel with the theme's global
   Swiper (vendored, loaded in theme.liquid) and handles ADD buttons via
   /cart/add.js. Everything is scoped to each section instance — multiple
   instances work independently and nothing global is touched. */

(function () {
  'use strict';

  if (customElements.get('pdp-community-recommends')) return;

  class PdpCommunityRecommends extends HTMLElement {
    connectedCallback() {
      this.swiperEl = this.querySelector('.pdp-community-recommends__swiper');
      this.initCarousel();
      this.bindAddButtons();
    }

    disconnectedCallback() {
      if (this.swiper && typeof this.swiper.destroy === 'function') {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      if (this.cardSwipers) {
        this.cardSwipers.forEach(function (instance) {
          if (instance && typeof instance.destroy === 'function') instance.destroy(true, true);
        });
        this.cardSwipers = null;
      }
    }

    /* ── Carousel ───────────────────────────────────────────────────── */

    initCarousel() {
      if (!this.swiperEl) return;
      if (!this.swiperEl.querySelector('.swiper-slide')) return; // empty collection — nothing to init

      if (window.Swiper) {
        this.buildSwiper();
      } else {
        // Swiper loads deferred from theme.liquid; try again once everything
        // has loaded. Until then the CSS native-scroll fallback applies.
        window.addEventListener(
          'load',
          function () {
            if (window.Swiper && !this.swiper) this.buildSwiper();
          }.bind(this),
          { once: true }
        );
      }
    }

    buildSwiper() {
      var nav = this.ensureNav();
      this.swiper = new window.Swiper(this.swiperEl, {
        slidesPerView: 2,
        spaceBetween: 12,
        watchOverflow: true,
        navigation: {
          prevEl: nav.prev,
          nextEl: nav.next
        },
        breakpoints: {
          768: {
            slidesPerView: 2.4,
            spaceBetween: 14
          },
          1025: {
            slidesPerView: 4.2,
            spaceBetween: 16
          }
        }
      });
      this.buildCardSwipers();
    }

    /* Per-card image sliders with pagination dots. `nested: true` keeps an
       image swipe from also dragging the outer cards carousel. Cards with a
       single image have no [data-pcr-card-swiper] markup, so nothing inits. */
    buildCardSwipers() {
      if (this.cardSwipers) return; // already built (e.g. load-event retry)
      this.cardSwipers = [];
      var els = this.querySelectorAll('[data-pcr-card-swiper]');
      els.forEach(
        function (el) {
          this.cardSwipers.push(
            new window.Swiper(el, {
              nested: true,
              slidesPerView: 1,
              spaceBetween: 0,
              watchOverflow: true,
              pagination: {
                el: el.querySelector('.pdp-community-card__media-dots'),
                clickable: true
              }
            })
          );
        }.bind(this)
      );
    }

    /* Prev/next arrows, created here so the Liquid stays untouched.
       Shown on mobile only (CSS hides them at >=768px). */
    ensureNav() {
      var existing = this.querySelector('.pdp-community-recommends__nav');
      if (existing) {
        return {
          prev: existing.querySelector('.pdp-community-recommends__nav-prev'),
          next: existing.querySelector('.pdp-community-recommends__nav-next')
        };
      }

      var inner = this.querySelector('.pdp-community-recommends__inner');
      var wrap = document.createElement('div');
      wrap.className = 'pdp-community-recommends__nav';

      var chevron =
        '<svg viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      var prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'pdp-community-recommends__nav-prev';
      prev.setAttribute('aria-label', 'Previous products');
      prev.innerHTML = chevron;

      var next = document.createElement('button');
      next.type = 'button';
      next.className = 'pdp-community-recommends__nav-next';
      next.setAttribute('aria-label', 'Next products');
      next.innerHTML = chevron;

      wrap.appendChild(prev);
      wrap.appendChild(next);
      inner.insertBefore(wrap, this.swiperEl);

      return { prev: prev, next: next };
    }

    /* ── ADD buttons (scoped /cart/add.js) ──────────────────────────── */

    bindAddButtons() {
      var buttons = this.querySelectorAll('[data-pcr-add]');
      buttons.forEach(
        function (button) {
          button.addEventListener('click', this.addToCart.bind(this, button));
        }.bind(this)
      );
    }

    addToCart(button) {
      var variantId = Number(button.dataset.variantId);
      if (!variantId || button.classList.contains('is-loading')) return;

      var originalHtml = button.innerHTML;
      button.classList.add('is-loading');

      fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'cart/add.js'
        : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Add to cart failed');
          return response.json();
        })
        .then(function () {
          button.innerHTML = 'ADDED &#10003;';
          // Let the theme's pub/sub know, if available, so cart UI can react.
          try {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pdp-community-recommends' });
            }
          } catch (e) {
            /* pub/sub not available — silently skip */
          }
          setTimeout(function () {
            button.innerHTML = originalHtml;
            button.classList.remove('is-loading');
          }, 1600);
        })
        .catch(function () {
          button.innerHTML = 'TRY AGAIN';
          setTimeout(function () {
            button.innerHTML = originalHtml;
            button.classList.remove('is-loading');
          }, 1600);
        });
    }
  }

  customElements.define('pdp-community-recommends', PdpCommunityRecommends);
})();
